<template>
  <div class="not-found-page">
    <div class="container">
      <div class="not-found-content animate__animated animate__fadeInUp">
        <!-- 404 Illustration -->
        <div class="error-illustration">
          <div class="error-code">404</div>
          <div class="error-graphic">
            <i class="fas fa-search"/>
            <div class="search-lines">
              <div class="line line-1"/>
              <div class="line line-2"/>
              <div class="line line-3"/>
            </div>
          </div>
        </div>

        <!-- Error Message -->
        <div class="error-message">
          <h1>Page Not Found</h1>
          <p>The page you're looking for doesn't exist or has been moved to a secure location.</p>

          <!-- Security Note for Technical Pages -->
          <div class="security-note" v-if="showSecurityNote">
            <i class="fas fa-shield-alt"/>
            <div>
              <strong>Restricted Access:</strong>
              <span>Some pages require authentication. Please log in to access technical information.</span>
            </div>
          </div>
        </div>

        <!-- Quick Actions -->
        <div class="quick-actions">
          <router-link to="/" class="btn btn-primary">
            <i class="fas fa-home"/>
            Back to Home
          </router-link>

          <button class="btn btn-secondary" @click="goBack">
            <i class="fas fa-arrow-left"/>
            Go Back
          </button>

          <button class="btn btn-outline" @click="showAuthModal" v-if="!isAuthenticated">
            <i class="fas fa-sign-in-alt"/>
            Login
          </button>
        </div>

        <!-- Helpful Links -->
        <div class="helpful-links">
          <h3>Looking for something specific?</h3>
          <div class="links-grid">
            <router-link to="/#features" class="help-link">
              <i class="fas fa-rocket"/>
              <div>
                <strong>Platform Features</strong>
                <span>Explore our development environment capabilities</span>
              </div>
            </router-link>

            <router-link to="/pricing" class="help-link">
              <i class="fas fa-dollar-sign"/>
              <div>
                <strong>Pricing Plans</strong>
                <span>Find the right plan for your team</span>
              </div>
            </router-link>

            <router-link to="/contact" class="help-link">
              <i class="fas fa-headset"/>
              <div>
                <strong>Contact Support</strong>
                <span>Get help from our technical team</span>
              </div>
            </router-link>

            <a href="#" @click.prevent="showTechnicalInfo" class="help-link">
              <i class="fas fa-cogs"/>
              <div>
                <strong>Technical Docs</strong>
                <span>Architecture and implementation details</span>
              </div>
            </a>
          </div>
        </div>

        <!-- Search Suggestion -->
        <div class="search-suggestion">
          <h4>Can't find what you're looking for?</h4>
          <p>Try searching our documentation or contact our support team for assistance.</p>

          <div class="search-form">
            <div class="search-input-group">
              <input
                v-model="searchQuery"
                type="text"
                placeholder="Search documentation..."
                @keyup.enter="performSearch"
              >
              <button @click="performSearch" class="search-btn">
                <i class="fas fa-search"/>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'

export default {
  name: 'NotFound',
  data() {
    return {
      searchQuery: '',
      showSecurityNote: false
    }
  },
  computed: {
    ...mapState('auth', ['isAuthenticated'])
  },

  mounted() {
    this.checkForTechnicalPath()

    // Log 404 page access
    this.$store.dispatch('logSecurityEvent', {
      type: '404_page_access',
      original_path: this.$route.fullPath,
      referrer: document.referrer || 'direct',
      timestamp: new Date().toISOString()
    })
  },
  methods: {
    goBack() {
      if (window.history.length > 1) {
        this.$router.go(-1)
      } else {
        this.$router.push('/')
      }
    },

    showAuthModal() {
      this.$store.commit('setShowAuthModal', true)
    },

    showTechnicalInfo() {
      if (this.isAuthenticated) {
        this.$router.push('/technical')
      } else {
        this.showAuthModal()
      }
    },

    performSearch() {
      if (this.searchQuery.trim()) {
        // In a real implementation, this would search documentation
        this.$store.dispatch('showNotification', {
          type: 'info',
          message: `Searching for "${this.searchQuery}"... Documentation search will be implemented here.`
        })

        // Log search attempt
        this.$store.dispatch('logSecurityEvent', {
          type: '404_page_search',
          query: this.searchQuery,
          original_path: this.$route.fullPath
        })
      }
    },

    checkForTechnicalPath() {
      // Check if the 404 was triggered by accessing a technical/restricted path
      const technicalPaths = ['/technical', '/admin', '/api', '/docs', '/infrastructure']
      const currentPath = this.$route.fullPath.toLowerCase()

      this.showSecurityNote = technicalPaths.some(path =>
        currentPath.includes(path) || currentPath.includes('tech')
      )
    }
  }
}
</script>

<style scoped>
.not-found-page {
  min-height: 100vh;
  background: #0a2540;
  background-image: url('/media/recraft/textures/texture-grid.svg');
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 120px 20px 60px;
  color: white;
  position: relative;
}

.not-found-page::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(10, 37, 64, 0.9);
  z-index: 0;
}

.not-found-page .container {
  position: relative;
  z-index: 1;
}

.not-found-content {
  text-align: center;
  max-width: 800px;
  width: 100%;
}

/* Error Illustration */
.error-illustration {
  margin-bottom: 3rem;
  position: relative;
}

.error-code {
  font-size: 8rem;
  font-weight: 900;
  line-height: 1;
  background: linear-gradient(135deg, #1a7fd4 0%, #10b981 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 1rem;
}

.error-graphic {
  position: relative;
  display: inline-block;
  margin-bottom: 2rem;
}

.error-graphic i {
  font-size: 3rem;
  color: rgba(255, 255, 255, 0.8);
  animation: float 3s ease-in-out infinite;
}

.search-lines {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.line {
  position: absolute;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
  animation: searchScan 2s ease-in-out infinite;
}

.line-1 {
  width: 60px;
  height: 2px;
  top: -20px;
  left: -30px;
  animation-delay: 0s;
}

.line-2 {
  width: 40px;
  height: 2px;
  top: 0px;
  left: -20px;
  animation-delay: 0.5s;
}

.line-3 {
  width: 50px;
  height: 2px;
  top: 20px;
  left: -25px;
  animation-delay: 1s;
}

@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
}

@keyframes searchScan {
  0%, 100% { opacity: 0.3; transform: scaleX(1); }
  50% { opacity: 0.8; transform: scaleX(1.2); }
}

/* Error Message */
.error-message {
  margin-bottom: 3rem;
}

.error-message h1 {
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
}

.error-message p {
  font-size: 1.2rem;
  opacity: 0.9;
  margin-bottom: 2rem;
  line-height: 1.6;
}

.security-note {
  display: inline-flex;
  align-items: flex-start;
  gap: 1rem;
  background: rgba(255, 107, 107, 0.2);
  border: 1px solid rgba(255, 107, 107, 0.5);
  padding: 1rem 1.5rem;
  border-radius: 15px;
  text-align: left;
  max-width: 500px;
  margin: 0 auto 2rem;
}

.security-note i {
  color: #ff6b6b;
  font-size: 1.2rem;
  margin-top: 0.2rem;
  flex-shrink: 0;
}

.security-note strong {
  display: block;
  margin-bottom: 0.25rem;
  color: #ff6b6b;
}

.security-note span {
  opacity: 0.9;
  line-height: 1.4;
}

/* Quick Actions */
.quick-actions {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 4rem;
  flex-wrap: wrap;
}

/* Helpful Links */
.helpful-links {
  margin-bottom: 4rem;
}

.helpful-links h3 {
  font-size: 1.5rem;
  margin-bottom: 2rem;
  opacity: 0.9;
}

.links-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  max-width: 600px;
  margin: 0 auto;
}

.help-link {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 15px;
  text-decoration: none;
  color: white;
  transition: all 0.3s ease;
}

.help-link:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateY(-2px);
  color: white;
}

.help-link i {
  font-size: 1.5rem;
  color: #1a7fd4;
  flex-shrink: 0;
}

.help-link strong {
  display: block;
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.help-link span {
  font-size: 0.9rem;
  opacity: 0.8;
  line-height: 1.3;
}

/* Search Suggestion */
.search-suggestion {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 20px;
  padding: 2rem;
}

.search-suggestion h4 {
  font-size: 1.3rem;
  margin-bottom: 0.5rem;
  color: #1a7fd4;
}

.search-suggestion p {
  opacity: 0.9;
  margin-bottom: 1.5rem;
  line-height: 1.5;
}

.search-form {
  max-width: 400px;
  margin: 0 auto;
}

.search-input-group {
  display: flex;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50px;
  padding: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.search-input-group input {
  flex: 1;
  padding: 0.8rem 1.5rem;
  background: transparent;
  border: none;
  color: white;
  font-size: 1rem;
  outline: none;
}

.search-input-group input::placeholder {
  color: rgba(255, 255, 255, 0.6);
}

.search-btn {
  background: #1a7fd4;
  color: white;
  border: none;
  border-radius: 50%;
  width: 45px;
  height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.search-btn:hover {
  background: #3b9ae8;
  transform: scale(1.05);
}

/* Responsive Design */
@media (max-width: 768px) {
  .not-found-page {
    padding: 100px 15px 40px;
  }

  .error-code {
    font-size: 6rem;
  }

  .error-message h1 {
    font-size: 2.5rem;
  }

  .error-message p {
    font-size: 1.1rem;
  }

  .quick-actions {
    flex-direction: column;
    align-items: center;
  }

  .links-grid {
    grid-template-columns: 1fr;
  }

  .help-link {
    flex-direction: column;
    text-align: center;
    gap: 0.75rem;
  }

  .security-note {
    flex-direction: column;
    text-align: center;
    gap: 0.75rem;
  }
}

@media (max-width: 480px) {
  .error-code {
    font-size: 4rem;
  }

  .error-message h1 {
    font-size: 2rem;
  }

  .helpful-links h3 {
    font-size: 1.3rem;
  }

  .search-suggestion {
    padding: 1.5rem;
  }

  .search-input-group {
    flex-direction: column;
    gap: 0.5rem;
    border-radius: 15px;
  }

  .search-btn {
    width: 100%;
    border-radius: 10px;
    height: 50px;
  }
}
</style>
